import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../GL/GeneralLoanDueReport.css';

const TBDLoanPartnerwiseDueReport = () => {
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    if (selectedPartner) {
      fetchReportData();
    } else {
      setReportData([]);
    }
  }, [selectedPartner]);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${baseUrl}/tbd-partners`);
      setPartners(response.data || []);
    } catch (error) {
      console.error('Error fetching partners:', error);
      alert('Error fetching partners. Please try again.');
    }
  };

  const fetchReportData = async () => {
    if (!selectedPartner) {
      setReportData([]);
      return;
    }

    setLoading(true);
    try {
      const params = { partner: selectedPartner };
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const response = await axios.get(`${baseUrl}/api/tbd-loan-partnerwise-due-report`, { params });
      setReportData(response.data || []);
    } catch (error) {
      console.error('Error fetching due report:', error);
      alert('Error fetching due report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = () => {
    fetchReportData();
  };

  const handleReset = () => {
    setFromDate('');
    setToDate('');
    if (selectedPartner) {
      fetchReportData();
    }
  };

  const handlePartnerChange = (e) => {
    setSelectedPartner(e.target.value);
  };

  const handleBack = () => {
    window.history.back();
  };

  const formatDateForPrint = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formatNumber = (num) => {
    return parseFloat(num || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  const handlePrint = () => {
    if (!selectedPartner || reportData.length === 0) {
      alert('Please select a partner and load data before printing.');
      return;
    }

    const printDate = new Date().toLocaleDateString('en-GB');
    const grandTotal = reportData.reduce((sum, row) => sum + (row.outstanding_balance || 0), 0);

    const installmentHeaders = Array.from({ length: 12 }, (_, i) => {
      const num = i + 1;
      return `${num}${getOrdinalSuffix(num)} Instl`;
    });

    const tableRows = reportData.map(row => {
      // TBD loans don't have specific installment dates, so we'll leave installments empty
      const installments = Array.from({ length: 12 }, () => ({ date: '', amount: '' }));

      return {
        account_no: row.account_no,
        loanee_name: row.loanee_name || '',
        phone_debtor: row.phone_debtor || '',
        guarantor_name: row.guarantor_name || '',
        phone_guarantor: row.phone_guarantor || '',
        loan_date: formatDateForPrint(row.loan_start_date),
        loan_amount: formatNumber(row.loan_amount),
        outstanding_balance: formatNumber(row.outstanding_balance),
        unpaid_days: row.unpaid_days || 0,
        installments
      };
    });

    const printContent = `
<!DOCTYPE html>
<html>
<head>
  <title>TBD Loan Partnerwise Due Report</title>
  <style>
    @media print {
      @page {
        size: A4 landscape;
        margin: 10mm;
      }
    }
    body {
      font-family: Arial, sans-serif;
      margin: 0;
      padding: 10px;
      font-size: 11px;
    }
    .header {
      text-align: center;
      margin-bottom: 10px;
    }
    .company-name {
      font-size: 24px;
      font-weight: bold;
      color: #d32f2f;
      margin-bottom: 5px;
    }
    .location {
      font-size: 14px;
      margin-bottom: 5px;
    }
    .report-title {
      font-size: 18px;
      font-weight: bold;
      color: #1976d2;
      margin-bottom: 10px;
    }
    .meta-info {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
      font-size: 12px;
    }
    .partner-name {
      font-weight: bold;
    }
    .print-date {
      font-weight: bold;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
      font-size: 10px;
    }
    th, td {
      border: 1px solid #000;
      padding: 4px;
      text-align: center;
      vertical-align: middle;
    }
    th {
      background-color: #f0f0f0;
      font-weight: bold;
      font-size: 10px;
    }
    .ac-no {
      width: 60px;
    }
    .names-col {
      width: 180px;
      text-align: left;
      padding: 4px;
    }
    .date-loan-col {
      width: 100px;
    }
    .instl-col {
      width: 70px;
      font-size: 9px;
    }
    .bal-amt-col {
      width: 80px;
      font-weight: bold;
    }
    .names-content {
      line-height: 1.4;
    }
    .names-content .loanee {
      font-weight: bold;
    }
    .names-content .phone {
      font-size: 9px;
      color: #555;
    }
    .instl-cell {
      padding: 2px;
      line-height: 1.3;
    }
    .footer {
      margin-top: 15px;
      display: flex;
      justify-content: space-between;
      font-size: 10px;
    }
    .end-statement {
      text-align: center;
      margin-top: 10px;
      font-weight: bold;
      font-size: 11px;
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="company-name">SHREE SIDDHARAMESHWAR FINANCE AND LEASING</div>
    <div class="location">Kasgi</div>
    <div class="report-title">-: TBD Loan Due List :-</div>
  </div>
  
  <div class="meta-info">
    <div class="partner-name">Partner Name :- ${selectedPartner}</div>
    <div class="print-date">Print Date ${printDate}</div>
  </div>

  <table>
    <thead>
      <tr>
        <th class="ac-no">A/C No</th>
        <th class="names-col">Name's</th>
        <th class="date-loan-col">Date/Loan</th>
        ${installmentHeaders.map(h => `<th class="instl-col">${h}</th>`).join('')}
        <th class="bal-amt-col">Bal Amt</th>
      </tr>
    </thead>
    <tbody>
      ${tableRows.map(row => `
        <tr>
          <td>${row.account_no}</td>
          <td class="names-col">
            <div class="names-content">
              <div class="loanee">L ${row.loanee_name}</div>
              <div class="phone">${row.phone_debtor}</div>
              <div class="guarantor">S ${row.guarantor_name}</div>
              <div class="phone">${row.phone_guarantor}</div>
            </div>
          </td>
          <td class="date-loan-col">
            <div>${row.loan_date}</div>
            <div style="font-weight: bold; margin-top: 2px;">${row.loan_amount}</div>
            <div style="font-size: 9px; color: #666; margin-top: 2px;">Unpaid: ${row.unpaid_days} days</div>
          </td>
          ${row.installments.map(inst => `
            <td class="instl-cell"></td>
          `).join('')}
          <td class="bal-amt-col">${row.outstanding_balance}</td>
        </tr>
      `).join('')}
      <tr style="background-color: #f0f0f0; font-weight: bold;">
        <td colspan="15" style="text-align: right; padding-right: 10px;">GRAND TOTAL:</td>
        <td class="bal-amt-col">${formatNumber(grandTotal)}</td>
      </tr>
    </tbody>
  </table>
</body>
</html>
    `;

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className="due-report-container">
      <div className="due-report-header">
        <h2>TBD Loan Partnerwise Due Report</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          {selectedPartner && reportData.length > 0 && (
            <button onClick={handlePrint} className="print-btn" style={{
              padding: '10px 20px',
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              Print
            </button>
          )}
          <button onClick={handleBack} className="back-btn">Back</button>
        </div>
      </div>

      <div className="date-filter-section">
        <div className="date-inputs">
          <div className="date-input-group">
            <label>Partner:</label>
            <select
              value={selectedPartner}
              onChange={handlePartnerChange}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                fontSize: '14px',
                width: '200px'
              }}
            >
              <option value="">Select Partner</option>
              {partners.map((partner, index) => (
                <option key={index} value={partner}>
                  {partner}
                </option>
              ))}
            </select>
          </div>
          <div className="date-input-group">
            <label>From Date:</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="date-input-group">
            <label>To Date:</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className="date-actions">
            <button onClick={handleDateFilter} className="filter-btn" disabled={!selectedPartner}>
              Filter
            </button>
            <button onClick={handleReset} className="reset-btn">
              Reset
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="report-table-container">
          <table className="due-report-table">
            <thead>
              <tr>
                <th>Account No</th>
                <th>Loanee Name & Phone</th>
                <th>Guarantor Name & Phone</th>
                <th>Unpaid Days</th>
                <th>Outstanding Balance</th>
              </tr>
            </thead>
            <tbody>
              {!selectedPartner ? (
                <tr>
                  <td colSpan="5" className="no-data">Please select a partner to view the report</td>
                </tr>
              ) : reportData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">No due records found for the selected partner</td>
                </tr>
              ) : (
                reportData.map((row) => (
                  <tr key={row.account_no}>
                    <td>{row.account_no}</td>
                    <td className="name-phone-cell">
                      <div className="name">{row.loanee_name}</div>
                      <div className="phone">{row.phone_debtor}</div>
                    </td>
                    <td className="name-phone-cell">
                      <div className="name">{row.guarantor_name}</div>
                      <div className="phone">{row.phone_guarantor}</div>
                    </td>
                    <td className="unpaid-days">
                      {row.unpaid_days} days
                    </td>
                    <td className="outstanding-balance">
                      ₹{row.outstanding_balance.toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TBDLoanPartnerwiseDueReport;

